import { Trash2 } from "lucide-react";
import { useCallback } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from "react-hook-form";
import AppButton from "../../../../components/ui/AppButton";
import AppInputField from "../../../../components/ui/AppInputField";
import { removeAfterDash } from "../../../../lib/utils";
import CrewDropdownMenu from "../../../driver/ui/CrewDropDownMenu";
import DriverDropdownMenu from "../../../driver/ui/DriverDropdownMenu";

interface DriverCrewFormData {
  fromLocation: string;
  toLocation: string;
  approximateDistance: number;
  totalExpectedIncome: number;
  totalIncomeEarned: number;
  totalPassengers: number;
  perPassengerRate: number;
  driverId: string;
  vehicleId: string;
}

interface CrewMemberInput {
  crewType: string;
  id: string;
  name: string;
}

interface DriverCrewStepProps {
  control: Control<DriverCrewFormData>;
  errors: FieldErrors<DriverCrewFormData>;
  crewMembers: CrewMemberInput[];
  setCrewMembers: React.Dispatch<React.SetStateAction<CrewMemberInput[]>>;
  setValue: UseFormSetValue<DriverCrewFormData>;
  onNext: () => void;
  onBack: () => void;
}

const DriverCrewStep = ({
  control,
  errors,
  crewMembers,
  setCrewMembers,
  setValue,
  onNext,
  onBack,
}: DriverCrewStepProps) => {
  const handleAddCrewMember = () => {
    setCrewMembers([...crewMembers, { crewType: "", id: "", name: "" }]);
  };

  const handleRemoveCrewMember = (index: number) => {
    setCrewMembers(crewMembers.filter((_, i) => i !== index));
  };

  const handleUpdateCrewMember = (
    index: number,
    field: keyof CrewMemberInput,
    value: string,
  ) => {
    const updated = [...crewMembers];
    updated[index] = { ...updated[index], [field]: value };
    setCrewMembers(updated);
  };

  const handleUpdateCrewMemberMultiple = useCallback(
    (index: number, updates: Partial<CrewMemberInput>) => {
      setCrewMembers((prev) =>
        prev.map((crew, i) => (i === index ? { ...crew, ...updates } : crew)),
      );
    },
    [],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        {/* Driver Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Driver</h3>
          <Controller
            control={control}
            name="driverId"
            render={({ field }) => (
              <DriverDropdownMenu
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  setValue("driverId", value);
                }}
                error={errors.driverId?.message}
                showLabel={false}
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
              />
            )}
          />
        </div>

        {/* Crew Section */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold">
              Crew Members{" "}
              <span className="text-gray-500 font-normal">(Optional)</span>
            </h3>
          </div>

          {crewMembers.map((crew, index) => (
            <div key={index} className="space-y-2">
              <div className="flex gap-2 items-center">
                <AppInputField
                  type="text"
                  value={crew.crewType}
                  onChange={(e) =>
                    handleUpdateCrewMember(index, "crewType", e.target.value)
                  }
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-[15rem] mt-2"
                  placeholder="Enter crew type"
                />
                <CrewDropdownMenu
                  value={crew.id}
                  onChange={(value) =>
                    handleUpdateCrewMemberMultiple(index, {
                      id: value?.id || "",
                      name: removeAfterDash(value?.name) || "",
                    })
                  }
                  showLabel={false}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                  placeholder="Select crew member"
                />

                <div className="w-[80px] bg-red-100 flex items-center justify-center h-[48px] rounded-[10px]">
                  <button
                    type="button"
                    onClick={() => handleRemoveCrewMember(index)}
                    className="text-red-500 hover:text-red-600 transition-colors p-1"
                    aria-label="Remove crew member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddCrewMember}
            className="text-secondary hover:text-primary transition-colors text-[13px] font-semibold"
          >
            + Add more
          </button>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-start items-start space-x-4 pt-4">
        <AppButton
          type="button"
          onClick={onBack}
          className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-[15px]"
        >
          Back
        </AppButton>

        <AppButton
          type="button"
          onClick={onNext}
          className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px]"
        >
          Continue
        </AppButton>
      </div>
    </div>
  );
};

export default DriverCrewStep;
